/**
 * Document Import Service
 * API service layer for document import operations.
 */

import HttpService from '@/utils/httpService';
import { objectToQueryParams } from '@/scripts/js/forgeService';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';

export default {
  /**
   * Get documents with filters and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.itemsPerPage - Items per page
   * @param {string} params.sortBy - Sort field
   * @param {boolean} params.sortDesc - Sort descending
   * @param {string} [params.assetId] - Filter by facility ID
   * @param {string} [params.facilityGroupId] - Filter by facility group ID
   * @param {string} [params.documentTypeId] - Filter by document type ID
   * @returns {Promise<{items: Array, total: number}>}
   */
  async getDocuments(params) {
    try {
      const response = await HttpService.get({
        url: `api/documents/TypeHead?${objectToQueryParams(params)}`,
      });

      const { items, total } = response.data;

      // Process items - add URL from storageReference
      items.forEach(doc => {
        doc.originalLinkedForgeTagsIds = [...(doc.forgeTagsIds ?? [])];
        doc.url = decodeURI(`${doc.storageReference}`);
      });

      return { items, total };
    } catch (err) {
      debugError('[documentImportService]', 'getDocuments failed:', err);
      throw err;
    }
  },

  /**
   * Upload files to blob storage
   * @param {File[]} files - Files to upload
   * @returns {Promise<Array<{fileName: string, storageReference: string, size: number}>>}
   */
  async uploadFiles(files) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await HttpService.post({
        url: 'api/documents/uploadFiles',
        data: formData,
      });

      debugLog('[documentImportService]', 'Uploaded files:', response.data?.length);
      return response.data;
    } catch (err) {
      debugError('[documentImportService]', 'uploadFiles failed:', err);
      throw err;
    }
  },

  /**
   * Create multiple documents with metadata (bulk)
   * @param {Array<Object>} documents - Documents to create
   * @returns {Promise<Array>}
   */
  async createDocuments(documents) {
    return (await this.createDocumentsWithResponse(documents)).data;
  },

  /**
   * Same as createDocuments, keeping the response headers (X-Document-Version-Duplicates).
   * @returns {Promise<{ data: Array, headers: Object }>}
   */
  async createDocumentsWithResponse(documents) {
    try {
      const response = await HttpService.post({
        url: 'api/documents/AddDocuments',
        data: documents,
      });

      debugLog('[documentImportService]', 'Created documents:', response.data?.length);
      return { data: response.data, headers: response.headers ?? {} };
    } catch (err) {
      debugError('[documentImportService]', 'createDocuments failed:', err);
      throw err;
    }
  },

  /**
   * Delete multiple documents by IDs
   * @param {string[]} ids - Document IDs to delete
   * @returns {Promise<boolean>}
   */
  async deleteDocuments(ids) {
    try {
      await HttpService.delete({
        url: 'api/documents/DeleteDocuments',
        data: ids,
      });

      debugLog('[documentImportService]', 'Deleted documents:', ids.length);
      return true;
    } catch (err) {
      debugError('[documentImportService]', 'deleteDocuments failed:', err);
      throw err;
    }
  },

  /**
   * Get document by ID
   * @param {string} id - Document ID
   * @returns {Promise<Object>}
   */
  async getDocumentById(id) {
    try {
      const response = await HttpService.get({
        url: `api/documents/${id}`,
      });

      const doc = response.data;
      doc.originalLinkedForgeTagsIds = [...(doc.forgeTagsIds ?? [])];
      doc.url = decodeURI(`${doc.storageReference}`);

      return doc;
    } catch (err) {
      debugError('[documentImportService]', 'getDocumentById failed:', err);
      throw err;
    }
  },

  /**
   * Update a document
   * @param {Object} document - Document data to update
   * @returns {Promise<Object>}
   */
  async updateDocument(document) {
    // Don't send storageReference/url on updates - they only change with new file uploads
    const { storageReference, url, ...updateData } = document;

    try {
      const response = await HttpService.put({
        url: `api/documents/${document.id}`,
        data: updateData,
      });

      return response.data;
    } catch (err) {
      debugError('[documentImportService]', 'updateDocument failed:', err);
      throw err;
    }
  },

  /**
   * Get document types for dropdown
   * @returns {Promise<Array>}
   */
  async getDocumentTypes() {
    try {
      const response = await HttpService.get({
        url: 'api/document-types',
        params: {
          page: -1,
          itemsPerPage: -1,
          sortBy: 'Name',
          sortDesc: false,
          isForge: true,
        },
      });

      return response.data;
    } catch (err) {
      debugError('[documentImportService]', 'getDocumentTypes failed:', err);
      throw err;
    }
  },
};
